import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const CATEGORY_LABELS = { BUG: 'Bug', IDEA: 'Idea', COMPLAINT: 'Complaint', OTHER: 'Help', CONTACT: 'Enquiry' };
const categoryBadge = (category) => ({ BUG: 'danger', IDEA: 'primary', COMPLAINT: 'warning', OTHER: 'secondary' }[category] || 'secondary');
const statusBadge = (status) => ({ NEW: 'warning', IN_PROGRESS: 'primary', RESOLVED: 'info', REOPEN: 'danger', CLOSED: 'success' }[status] || 'secondary');
const statusLabel = (item) => item.statusLabel || item.status;

const FILTERS = [
  { key: 'ALL', label: 'All requests', test: () => true },
  { key: 'OPEN', label: 'Open', test: (item) => item.status !== 'CLOSED' },
  { key: 'WAITING', label: 'Waiting for me (resolved)', test: (item) => item.status === 'RESOLVED' },
  { key: 'CLOSED', label: 'Closed', test: (item) => item.status === 'CLOSED' },
];

/** What a request is about, in a line: the first line of what the person wrote. */
export function subjectOf(item) {
  const first = String(item.message || '').split('\n').map((l) => l.trim()).find((l) => l) || '(no text)';
  return first.length > 80 ? `${first.slice(0, 79).trimEnd()}…` : first;
}

const WHO = { ADMIN: 'iBigDo team', SYSTEM: 'System', USER: 'You' };

/** The latest thing on a request, and who did it: the team, the system (a status change) or the person. */
export function lastUpdateOf(item) {
  const messages = item.messages || [];
  if (messages.length > 0) {
    const last = messages[messages.length - 1];
    return { at: last.createdAt, by: WHO[last.authorType] || 'You' };
  }
  if (item.response) return { at: item.respondedAt || item.createdAt, by: WHO.ADMIN };
  return { at: item.createdAt, by: WHO.USER };
}

/**
 * Help & Support > My requests: the person's requests as a table (newest activity first, those with a new reply on top), and the whole
 * conversation of one in a dialog opened from its row. Accepting or rejecting a fix is done in the dialog.
 */
function MyRequests({ items, loading, onRefresh, isNewReply, openId, onOpen, onClose, onAccept, onReject, actionLoadingId }) {
  const [filter, setFilter] = useState('ALL');

  const rows = useMemo(() => {
    const test = (FILTERS.find((f) => f.key === filter) || FILTERS[0]).test;
    return items
      .filter(test)
      .map((item) => ({ item, last: lastUpdateOf(item) }))
      .sort((a, b) => {
        const byNew = Number(isNewReply(b.item)) - Number(isNewReply(a.item));
        if (byNew !== 0) return byNew;
        return String(b.last.at || '').localeCompare(String(a.last.at || ''));
      });
  }, [items, filter, isNewReply]);

  const open = items.find((item) => String(item.id) === String(openId)) || null;

  return (
    <Card className="mb-4" data-testid="my-requests">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">My requests</h5>
        <div className="d-flex align-items-center gap-2">
          <Form.Select size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Show" style={{ width: 'auto' }}>
            {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </Form.Select>
          <Button size="sm" variant="outline-secondary" onClick={onRefresh}>Refresh</Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {loading && items.length === 0 ? (
          <div className="d-flex justify-content-center py-4"><Spinner animation="border" role="status" /></div>
        ) : items.length === 0 ? (
          <p className="text-muted m-3">You have not sent any requests yet. Use the Ask &amp; contact tab to send one.</p>
        ) : rows.length === 0 ? (
          <p className="text-muted m-3">Nothing here. Choose another view above.</p>
        ) : (
          <Table hover responsive className="mb-0 align-middle" data-testid="my-requests-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Subject</th>
                <th className="d-none d-md-table-cell">Type</th>
                <th>Status</th>
                <th>Last update</th>
                <th className="d-none d-md-table-cell">Updated by</th>
                <th className="d-none d-lg-table-cell">Sent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ item, last }) => (
                <tr
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen(item.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(item.id); } }}
                  className={String(item.id) === String(openId) ? 'table-active' : ''}
                  data-testid={`request-row-${item.id}`}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="text-muted">{item.id}</td>
                  <td style={{ maxWidth: '26rem', minWidth: '11rem' }}>
                    <span className="fw-semibold">{subjectOf(item)}</span>
                    {isNewReply(item) && <Badge bg="danger" className="ms-2">New reply</Badge>}
                  </td>
                  <td className="d-none d-md-table-cell"><Badge bg={categoryBadge(item.category)}>{CATEGORY_LABELS[item.category] || item.category}</Badge></td>
                  <td><Badge bg={statusBadge(item.status)}>{statusLabel(item)}</Badge></td>
                  <td className="text-nowrap">{last.at ? formatDateTime(last.at) : ''}</td>
                  <td className="text-nowrap d-none d-md-table-cell">{last.by}</td>
                  <td className="text-nowrap d-none d-lg-table-cell">{item.createdAt ? formatDate(String(item.createdAt).slice(0, 10)) : ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={Boolean(open)} onHide={onClose} size="lg" scrollable centered aria-labelledby="request-dialog-title">
        {open && (
          <>
            <Modal.Header closeButton>
              <Modal.Title id="request-dialog-title" className="h5">
                <span className="text-muted me-2">#{open.id}</span>{subjectOf(open)}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Badge bg={categoryBadge(open.category)}>{CATEGORY_LABELS[open.category] || open.category}</Badge>
                <Badge bg={statusBadge(open.status)}>{statusLabel(open)}</Badge>
                {isNewReply(open) && <Badge bg="danger">New reply</Badge>}
                <small className="text-muted">Sent {open.createdAt ? formatDateTime(open.createdAt) : ''}</small>
              </div>

              {(open.messages || []).length > 0 ? (
                <div data-testid="request-thread">
                  {open.messages.map((msg) => (
                    <div key={msg.id} className="mb-3 pb-3 border-bottom">
                      <div className="d-flex justify-content-between gap-2">
                        <small className="fw-semibold">
                          {msg.authorType === 'ADMIN' ? `iBigDo · ${msg.authorName || ''}` : msg.authorType === 'SYSTEM' ? 'System' : 'You'}
                        </small>
                        <small className="text-muted text-nowrap">{msg.createdAt ? formatDateTime(msg.createdAt) : ''}</small>
                      </div>
                      <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>{open.message}</div>
                  {open.response && (
                    <Alert variant="info" className="py-2">
                      <strong>iBigDo reply</strong>
                      {open.respondedAt && <small className="text-muted ms-2">{formatDateTime(open.respondedAt)}</small>}
                      <div className="mt-1">{open.response}</div>
                    </Alert>
                  )}
                </>
              )}

              {(!open.messages || open.messages.length <= 1) && !open.response && open.status !== 'CLOSED' && (
                <small className="text-muted d-block">
                  {open.status === 'NEW' && 'Sent. Waiting for the team to pick it up.'}
                  {open.status === 'IN_PROGRESS' && 'In progress with the iBigDo team.'}
                  {open.status === 'REOPEN' && 'Reopened. Waiting for further work.'}
                </small>
              )}

              {open.status === 'RESOLVED' && (
                <small className="text-muted d-block">
                  Awaiting your decision on the proposed resolution.
                  {open.autoCloseOn ? ` It closes by itself on ${formatDate(open.autoCloseOn)} if we do not hear from you; you can always send a new request.` : ''}
                </small>
              )}
            </Modal.Body>
            <Modal.Footer>
              {open.canAccept && open.canReject && (
                <>
                  <Button variant="success" disabled={actionLoadingId === open.id} onClick={() => onAccept(open.id)}>
                    {actionLoadingId === open.id ? 'Updating...' : 'Accept & close'}
                  </Button>
                  <Button variant="outline-danger" disabled={actionLoadingId === open.id} onClick={() => onReject(open.id)}>
                    Reject &amp; reopen
                  </Button>
                </>
              )}
              <Button variant="secondary" onClick={onClose}>Close</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Card>
  );
}

export default MyRequests;
